import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { subscriptionApi } from '@/api/client';

/****************
 * The user's own book, across every session.
 *
 * Guillaume, 2026-09-03: the dashboard's first section is HIS data — his open
 * positions, his long/short split, his asset mix, his leverage. Not the traders
 * we track; those are section three.
 *
 * `/api/v1/replicator/positions` is already scoped to the signed-in user across
 * all their sessions, so this needs no new endpoint and, more importantly, no
 * per-session fan-out. Asking each session separately would be one request per
 * session on every dashboard load — the same N+1 that made this page unusable
 * once already.
 ****************/

export interface AssetExposure {
  symbol: string;
  notional: number;
  count: number;
  longNotional: number;
  shortNotional: number;
}

export interface PortfolioExposure {
  openCount: number;
  longCount: number;
  shortCount: number;
  longNotional: number;
  shortNotional: number;
  totalNotional: number;

  /** The four ratios, 0-100. Null when there is nothing to divide. */
  longCountRatio: number | null;
  shortCountRatio: number | null;
  longVolumeRatio: number | null;
  shortVolumeRatio: number | null;

  /**
   * Notional-weighted average leverage across open positions.
   *
   * Weighted, not a plain mean: a $50,000 position at 3x and a $200 position at
   * 100x average to 51x unweighted, which describes neither the book nor the
   * risk in it. Weighting by notional says what leverage the MONEY is actually
   * running at.
   */
  weightedLeverage: number | null;

  /** Biggest first. */
  byAsset: AssetExposure[];

  /** Realised PnL by direction, on the closed history only (Bull/Bear). */
  longPnl?: number;
  shortPnl?: number;
}

const EMPTY: PortfolioExposure = {
  openCount: 0,
  longCount: 0,
  shortCount: 0,
  longNotional: 0,
  shortNotional: 0,
  totalNotional: 0,
  longCountRatio: null,
  shortCountRatio: null,
  longVolumeRatio: null,
  shortVolumeRatio: null,
  weightedLeverage: null,
  byAsset: [],
};

export function usePortfolioPositions() {
  return useQuery({
    queryKey: ['portfolio', 'positions'],
    // No subscription id: every session at once, which is the whole point.
    queryFn: async () => (await subscriptionApi.getPositions()) || [],
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * Fold the raw positions into the numbers the section renders.
 *
 * ── Notional is measured AT ENTRY, deliberately ─────────────────────────────
 *
 * Current exposure would be size x mark price, which is the more precise
 * quantity — but the live price feed carries about twenty hardcoded symbols
 * while far more can be open (that is FD-9). Marking the symbols we have and
 * falling back to entry for the rest would produce a single total measured two
 * different ways, with no way to see which positions used which. One consistent
 * basis beats a more accurate one that silently mixes units, so every figure
 * here is entry notional and the UI says so.
 */
export function usePortfolioExposure(positions: any[] | undefined): PortfolioExposure {
  return useMemo(() => {
    if (!positions || positions.length === 0) return EMPTY;

    let longCount = 0;
    let shortCount = 0;
    let longNotional = 0;
    let shortNotional = 0;
    let leverageWeighted = 0;
    let leverageBasis = 0;
    const assets = new Map<string, AssetExposure>();

    for (const p of positions) {
      const size = Math.abs(Number(p.size) || 0);
      const entry = Number(p.entry_price) || 0;
      if (size <= 0 || entry <= 0) continue; // a position with no basis is not exposure

      const notional = size * entry;
      const isLong = String(p.side || '').toUpperCase() === 'LONG';
      const symbol = String(p.symbol || '—');

      if (isLong) {
        longCount += 1;
        longNotional += notional;
      } else {
        shortCount += 1;
        shortNotional += notional;
      }

      // Leverage of 0 means unset, not 1x. Including it as 1x would drag the
      // weighted average down by however much unenriched size is in the book.
      const lev = Number(p.leverage) || 0;
      if (lev > 0) {
        leverageWeighted += notional * lev;
        leverageBasis += notional;
      }

      const row = assets.get(symbol) || {
        symbol,
        notional: 0,
        count: 0,
        longNotional: 0,
        shortNotional: 0,
      };
      row.notional += notional;
      row.count += 1;
      if (isLong) row.longNotional += notional;
      else row.shortNotional += notional;
      assets.set(symbol, row);
    }

    const openCount = longCount + shortCount;
    const totalNotional = longNotional + shortNotional;

    return {
      openCount,
      longCount,
      shortCount,
      longNotional,
      shortNotional,
      totalNotional,
      longCountRatio: openCount > 0 ? (longCount / openCount) * 100 : null,
      shortCountRatio: openCount > 0 ? (shortCount / openCount) * 100 : null,
      longVolumeRatio: totalNotional > 0 ? (longNotional / totalNotional) * 100 : null,
      shortVolumeRatio: totalNotional > 0 ? (shortNotional / totalNotional) * 100 : null,
      weightedLeverage: leverageBasis > 0 ? leverageWeighted / leverageBasis : null,
      byAsset: [...assets.values()].sort((a, b) => b.notional - a.notional),
    };
  }, [positions]);
}

/****************
 * Equity over time.
 *
 * Reads hourly snapshots the backend writes on its own timer, so the series is
 * evenly spaced whether or not anyone has the dashboard open. Opening the page
 * never triggers a capture.
 ****************/
/**
 * The same three shapes, over closed trades.
 *
 * Returned in the PortfolioExposure shape so one set of charts can draw either
 * without knowing which it was given — a second set of chart components that
 * happened to look the same would be two things to keep looking the same.
 */
export function useHistoryExposure(scope: 'live' | 'paper' = 'live', credentials?: number[]) {
  const query = useQuery({
    queryKey: ['portfolio', 'history-exposure', scope, credentials?.join(',') ?? 'all'],
    queryFn: () => subscriptionApi.getHistoryExposure(scope, credentials),
    staleTime: 60_000,
  });

  const exposure = useMemo<PortfolioExposure>(() => {
    const d = query.data;
    if (!d || !d.trade_count) return EMPTY;
    const total = d.total_notional || 0;
    const count = (d.long_count || 0) + (d.short_count || 0);
    return {
      openCount: count,
      longCount: d.long_count || 0,
      shortCount: d.short_count || 0,
      longNotional: d.long_notional || 0,
      shortNotional: d.short_notional || 0,
      totalNotional: total,
      longCountRatio: count > 0 ? ((d.long_count || 0) / count) * 100 : null,
      shortCountRatio: count > 0 ? ((d.short_count || 0) / count) * 100 : null,
      longVolumeRatio: total > 0 ? ((d.long_notional || 0) / total) * 100 : null,
      shortVolumeRatio: total > 0 ? ((d.short_notional || 0) / total) * 100 : null,
      weightedLeverage: d.weighted_leverage > 0 ? d.weighted_leverage : null,
      longPnl: d.long_pnl ?? 0,
      shortPnl: d.short_pnl ?? 0,
      byAsset: (d.by_asset || [])
        .map((a: any) => ({
          symbol: a.symbol,
          notional: a.notional,
          count: a.count,
          longNotional: 0,
          shortNotional: 0,
        }))
        .sort((a: any, b: any) => b.notional - a.notional),
    };
  }, [query.data]);

  return {
    exposure,
    isLoading: query.isLoading,
    tradeCount: query.data?.trade_count ?? 0,
    groups: (query.data?.groups ?? null) as HistoryGroup[] | null,
    results: (query.data?.results ?? null) as HistoryResult[] | null,
  };
}

/** One (symbol, direction) row of the closed history, before summing. */
export interface HistoryGroup {
  symbol: string;
  direction: string;
  notional: number;
  leverage: number;
  trades: number;
  pnl: number;
}

/** One session's closed count, wins, losses and realised PnL on one symbol,
 *  counted as the session totals are (TradeAggFor on the server). */
export interface HistoryResult {
  subscription_id: number;
  symbol: string;
  closed: number;
  wins: number;
  losses: number;
  realized_pnl: number;
}

/**
 * The closed-history charts for the symbols `keep` accepts: the server's sum,
 * redone over its own rows (the Dashboard's asset-type choice, 2026-10-04).
 */
export function exposureFromHistoryGroups(groups: HistoryGroup[], keep: (symbol: string) => boolean): PortfolioExposure {
  let longCount = 0, shortCount = 0, longNotional = 0, shortNotional = 0, longPnl = 0, shortPnl = 0, levNotional = 0, levMargin = 0;
  const assets = new Map<string, AssetExposure>();
  for (const g of groups) {
    if (!keep(g.symbol)) continue;
    const dir = String(g.direction || '').toUpperCase();
    if (dir === 'LONG') { longCount += g.trades; longNotional += g.notional; longPnl += g.pnl; }
    else if (dir === 'SHORT') { shortCount += g.trades; shortNotional += g.notional; shortPnl += g.pnl; }
    // Total size over total margin, as the server does (2026-10-05).
    if (g.leverage > 0) { levNotional += g.notional; levMargin += g.notional / g.leverage; }
    const a = assets.get(g.symbol) || { symbol: g.symbol, notional: 0, count: 0, longNotional: 0, shortNotional: 0 };
    a.notional += g.notional;
    a.count += g.trades;
    assets.set(g.symbol, a);
  }
  const count = longCount + shortCount;
  if (count === 0) return EMPTY;
  const total = longNotional + shortNotional;
  return {
    openCount: count,
    longCount,
    shortCount,
    longNotional,
    shortNotional,
    totalNotional: total,
    longCountRatio: (longCount / count) * 100,
    shortCountRatio: (shortCount / count) * 100,
    longVolumeRatio: total > 0 ? (longNotional / total) * 100 : null,
    shortVolumeRatio: total > 0 ? (shortNotional / total) * 100 : null,
    weightedLeverage: levMargin > 0 ? levNotional / levMargin : null,
    longPnl,
    shortPnl,
    byAsset: [...assets.values()].sort((a, b) => b.notional - a.notional),
  };
}

export function useEquityHistory(
  range: '24h' | '7d' | '30d' | '90d' = '24h',
  scope: 'all' | 'live' | 'paper' = 'all',
) {
  return useQuery({
    // Scope is part of the key: paper and live are different series, and
    // sharing a cache entry would show one while claiming the other.
    queryKey: ['portfolio', 'equity', range, scope],
    queryFn: () => subscriptionApi.getEquityHistory(range, scope),
    /*
     * Thirty seconds, not five minutes.
     *
     * The old interval came with the note "snapshots land hourly; polling
     * faster only re-reads the same row", which was true when the response was
     * assembled entirely from the hourly series. It no longer is: `current`,
     * `open_positions` and `open_book` are rebuilt from a wallet refreshed
     * every minute and positions read every fifteen seconds. Polling at five
     * minutes threw that away and put an eight-minute-old equity on screen
     * while the server had a fresh one. The chart points below it are still
     * hourly and are unaffected by asking more often.
     */
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}
